import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePathHelper } from '@/hooks/usePathHelper'
import {
  REPORT_DATE_MODE,
  clampReportDateRange,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import {
  getClosedDaysReportFiltersFromSearch,
  closedDaysReportSearchSchema,
  type ClosedDaysReportFilters,
  type ClosedDaysReportSearch,
} from '@/lib/services/reports/reports.schema'
import {
  reportQueries,
  useClosedDaysReport,
  useExportClosedDaysReportMutation,
} from '@/lib/services/reports/reports.query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CalendarOff, Download } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { ClosedDaysReportResults } from './-results'

export const Route = createFileRoute('/_authenticated/$orgId/reports/closed-days/')({
  component: ClosedDaysReportPage,
  validateSearch: (search) => closedDaysReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
      return
    }

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(search, organization)

    await context.queryClient.ensureQueryData(
      reportQueries.closedDays({
        organizationId: params.orgId,
        filters,
      })
    )
  },
})

export function ClosedDaysReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization)
  const { data: report } = useClosedDaysReport(filters)
  const exportMutation = useExportClosedDaysReportMutation()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const hasExportData = (report?.locations?.length ?? 0) > 0

  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) => {
    startTransition(() => {
      void navigate({
        ...path.closedDays.index().using({ orgId }).link,
        search: {
          start_date,
          end_date,
          date_mode: date_mode ?? undefined,
        },
        replace: Boolean(search.start_date && search.end_date) || Boolean(search.date_mode),
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(
        response,
        `closed-days-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Failed to export closed days report'))
    }
  }

  return (
    <div className="pb-6">
      <AdminPageHeader
        icon={CalendarOff}
        title="Closed Days Report"
        description="Review scheduled pickup and delivery pauses across your active locations."
        className="mb-4 mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={filters.date_mode ?? REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                ...filters,
                ...datePickerBounds,
              }}
              now={organizationNow}
              onApply={applyFilters}
            />
            {hasExportData ? (
              <AdminHeaderActionButton
                type="button"
                variant="outline"
                onClick={handleExport}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4" />
                Export Spreadsheet
              </AdminHeaderActionButton>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <AdminHeaderActionButton type="button" variant="outline" disabled>
                      <Download className="h-4 w-4" />
                      Export Spreadsheet
                    </AdminHeaderActionButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>No active locations to export.</TooltipContent>
              </Tooltip>
            )}
          </>
        }
      />

      <ClosedDaysReportResults report={report} orgId={orgId} path={path} />
    </div>
  )
}

export function getReportFilters(
  search: ClosedDaysReportSearch,
  organization: Organization
): ClosedDaysReportFilters {
  const organizationNow = getOrganizationNow(organization?.timezone)
  const defaultFilters = getCurrentReportDateFilters(organizationNow)
  const { minDate, maxDate } = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const filters = getClosedDaysReportFiltersFromSearch(search, defaultFilters)

  return {
    ...filters,
    ...clampReportDateRange(filters, minDate, maxDate),
  }
}
